/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Rating,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { makeRequest } from '@ink-ai/portal/common/requestHelper';

const MAX_COMMENTS_LENGTH = 1000;
const CLOSE_AFTER_SUCCESS_MS = 1500;

type Ratings = {
  uiRating: number | null;
  qualityRating: number | null;
  formatRating: number | null;
};

const emptyRatings: Ratings = {
  uiRating: null,
  qualityRating: null,
  formatRating: null,
};

export const FeedbackDialog = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [ratings, setRatings] = useState<Ratings>(emptyRatings);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const dimensions: { key: keyof Ratings; label: string }[] = [
    { key: 'uiRating', label: t('feedback.uiRating') },
    { key: 'qualityRating', label: t('feedback.qualityRating') },
    { key: 'formatRating', label: t('feedback.formatRating') },
  ];

  const hasInput =
    !!ratings.uiRating ||
    !!ratings.qualityRating ||
    !!ratings.formatRating ||
    !!comments.trim();

  const reset = () => {
    setRatings(emptyRatings);
    setComments('');
    setSubmitting(false);
    setSubmitted(false);
    setError('');
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    setOpen(false);
    reset();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await makeRequest({
        method: 'POST',
        url: '/api/v1/feedback/rating',
        data: {
          ...(ratings.uiRating ? { uiRating: ratings.uiRating } : {}),
          ...(ratings.qualityRating
            ? { qualityRating: ratings.qualityRating }
            : {}),
          ...(ratings.formatRating
            ? { formatRating: ratings.formatRating }
            : {}),
          ...(comments.trim() ? { comments: comments.trim() } : {}),
        },
      });
      setSubmitted(true);
      setTimeout(handleClose, CLOSE_AFTER_SUCCESS_MS);
    } catch (submitError: any) {
      console.error(submitError);
      setError(t('feedback.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title={t('feedback.tooltip')}>
        <Button
          color="inherit"
          aria-label={t('feedback.tooltip')}
          onClick={() => setOpen(true)}
          startIcon={<RateReviewOutlinedIcon />}
          sx={{ mr: 1, textTransform: 'none' }}
        >
          {t('feedback.tooltip')}
        </Button>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        aria-labelledby="feedback-dialog-title"
      >
        {submitted ? (
          <DialogContent>
            <Stack alignItems="center" spacing={1.5} sx={{ py: 3 }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
              <Typography>{t('feedback.thanks')}</Typography>
            </Stack>
          </DialogContent>
        ) : (
          <>
            <DialogTitle
              id="feedback-dialog-title"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {t('feedback.title')}
              <IconButton onClick={handleClose} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('feedback.ratingPrompt')}
              </Typography>
              <Stack spacing={1.5}>
                {dimensions.map(({ key, label }) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="body2">{label}</Typography>
                    <Rating
                      name={key}
                      value={ratings[key]}
                      onChange={(_event, value) =>
                        setRatings((prev) => ({ ...prev, [key]: value }))
                      }
                    />
                  </Box>
                ))}
              </Stack>
              <Typography variant="body2" sx={{ mt: 3, mb: 1 }}>
                {t('feedback.comments')}
              </Typography>
              <TextField
                multiline
                minRows={3}
                fullWidth
                value={comments}
                onChange={(event) =>
                  setComments(event.target.value.slice(0, MAX_COMMENTS_LENGTH))
                }
                placeholder={t('feedback.commentsPlaceholder')}
                helperText={`${comments.length} / ${MAX_COMMENTS_LENGTH}`}
                slotProps={{ formHelperText: { sx: { textAlign: 'right' } } }}
              />
              {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {error}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="inherit">
                {t('common.cancel')}
              </Button>
              <Tooltip title={hasInput ? '' : t('feedback.emptyHint')}>
                <span>
                  <LoadingButton
                    variant="contained"
                    loading={submitting}
                    disabled={!hasInput}
                    onClick={handleSubmit}
                  >
                    {t('feedback.submit')}
                  </LoadingButton>
                </span>
              </Tooltip>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};
